import { useState } from 'react'
import {
	EuiFilterGroup,
	EuiFilterButton,
	EuiPopover,
	EuiSelectable,
	EuiFieldSearch,
} from '@equipmentshare/ds2'
import {
	ArrowUpSLineIcon,
	ArrowDownSLineIcon,
} from '@equipmentshare/ds2-icons'
import { useUsersSearchLazyQuery } from '../../../graphql'
import useCompanyId from 'hooks/useCompanyId'
import { useAuthenticatedUser } from 'hooks/useAuthenticatedUser'
import useInputDebounce from 'hooks/useInputDebounce'

export type AssignedToFilterProps = {
	selectedIds: string[],
	onChange: ( ids: string[] ) => void,
}

export default function AssignedToFilter( { selectedIds, onChange }: AssignedToFilterProps ) {
	const [ isPopoverOpen, setIsPopoverOpen ] = useState( false )
	const [ searchInput, setSearchInput ] = useState( '' )
	const [ searchResults, setSearchResults ] = useState<any[]>( [] )
	// Tracks full user objects for selected IDs so they remain visible after search is cleared
	const [ selectedUsers, setSelectedUsers ] = useState<any[]>( [] )

	const companyId = useCompanyId()
	const { id: currentUserId } = useAuthenticatedUser()

	const [ searchUsers ] = useUsersSearchLazyQuery()

	useInputDebounce( searchInput, 300, ( debounced: string ) => {
		if ( !debounced.trim() || !companyId ) {
			setSearchResults( [] )
			return
		}
		searchUsers( { variables: { companyId, search: debounced } } ).then( ( result ) => {
			setSearchResults( result.data?.usersSearch ?? [] )
		} )
	} )

	// "Assigned to me" is always the first option
	const meOption: any = {
		label: 'Assigned to me',
		value: currentUserId ?? '',
		checked: currentUserId && selectedIds.includes( currentUserId ) ? 'on' : undefined,
		disabled: !currentUserId,
	}

	// Merge search results with persisted selected users, deduplicated, excluding current user
	const allUsers: any[] = [
		...selectedUsers,
		...searchResults.filter( ( u: any ) =>
			!selectedUsers.some( ( s: any ) => String( s.user_id ) === String( u.user_id ) ) ),
	].filter( ( u: any ) => String( u.user_id ) !== currentUserId )

	const userOptions: any[] = allUsers.map( ( u: any ) => ( {
		label: `${u.first_name} ${u.last_name}`,
		value: String( u.user_id ),
		checked: selectedIds.includes( String( u.user_id ) ) ? 'on' : undefined,
	} ) )

	const options: any[] = [ meOption, ...userOptions ]

	const handleChange = (newOptions: any[]) => {
    const selected = newOptions
        .filter((o: any) => o.checked === 'on' && o.value)
        .map((o: any) => o.value as string)

		// Keep user objects for newly selected users so they persist after search clears
    const allKnownUsers = [...selectedUsers, ...searchResults].filter(
			// need to dedupe
      (u, index, self) => index === self.findIndex((s) => String(s.user_id) === String(u.user_id))
    )

    const nextSelectedUsers = allKnownUsers.filter((u: any) =>
        selected.includes(String(u.user_id))
    )

    setSelectedUsers(nextSelectedUsers)
    onChange(selected)
	}

	const closePopover = () => {
		setIsPopoverOpen( false )
		setSearchInput( '' )
		setSearchResults( [] )
	}

	const togglePopover = () => {
		setIsPopoverOpen( ( prev ) => {
			if ( prev ) {
				setSearchInput( '' )
				setSearchResults( [] )
			}
			return !prev
		} )
	}

	const button = (
		<EuiFilterButton
			iconType={isPopoverOpen ? ArrowUpSLineIcon : ArrowDownSLineIcon}
			iconSide="right"
			isSelected={isPopoverOpen}
			onClick={togglePopover}
			numActiveFilters={selectedIds.length}
			hasActiveFilters={selectedIds.length > 0}
		>
			Assigned To
		</EuiFilterButton>
	)

	return (
		<EuiFilterGroup>
			<EuiPopover
				button={button}
				isOpen={isPopoverOpen}
				closePopover={closePopover}
				panelPaddingSize="s"
				panelStyle={{ width: 280 }}
			>
				<EuiFieldSearch
					placeholder="Search users..."
					value={searchInput}
					onChange={( e ) => setSearchInput( e.target.value )}
					compressed
					fullWidth
				/>
				<EuiSelectable
					options={options}
					onChange={handleChange}
					searchable={false}
				>
					{( list ) => list}
				</EuiSelectable>
			</EuiPopover>
		</EuiFilterGroup>
	)
}
